import { useMemo, useCallback, useState } from "react";
import { Edit2, Trash, Plus, X } from "lucide-react";
// import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DynamicModuleService,
  SchemaService,
  DropdownService,
  PtsProjectService,
} from "@/services/APIs";
import { notify } from "@/components/common/Notification";
import CustomButton from "@/components/common/Button";
import { Popconfirm, Collapse, Pagination } from "antd";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { getEmployeeId } from "@/lib/storeManager";
import { buildSpentHoursStatusOptions } from "./spentHoursStatusHelpers";
import { spentHoursStatusPill } from "./spentHoursStatusPill";
import "react-quill/dist/quill.snow.css";

const SPENT_HOURS_SCHEMA_NAME = "PTS Spent Hours";
const TASK_SCHEMA_NAME = "PTS Task";
// const TASK_CATEGORY_SCHEMA_NAME = "PTS Task Category";

interface ActivityLogTabProps {
  selectedEmployeeId?: string | null;
}

const ActivityLogTab = ({ selectedEmployeeId }: ActivityLogTabProps) => {
  const queryClient = useQueryClient();
  // Use selectedEmployeeId prop if provided, otherwise fallback to getEmployeeId()
  const employeeId = selectedEmployeeId ?? getEmployeeId();

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(300);

  // Calculate default dates
  // Start date: 6 days before today (7 days total including today)
  // End date: Today
  // Example: If today is Dec 31, start date is Dec 25 (Dec 25, 26, 27, 28, 29, 30, 31 = 7 days)
  const getDefaultDates = useCallback(() => {
    const today = new Date();

    // End date: Today
    const endDateStr = today.toISOString().split("T")[0]; // YYYY-MM-DD format

    // Start date: 6 days before today (to get exactly 7 days including today)
    // Using milliseconds to avoid date calculation issues
    const sixDaysInMs = 6 * 24 * 60 * 60 * 1000;
    const startDate = new Date(today.getTime() - sixDaysInMs);
    const startDateStr = startDate.toISOString().split("T")[0]; // YYYY-MM-DD format

    return {
      startDate: startDateStr,
      endDate: endDateStr,
    };
  }, []);

  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState<string>(defaultDates.startDate);
  const [endDate, setEndDate] = useState<string>(defaultDates.endDate);
  const [isStartDateChanged, setIsStartDateChanged] = useState<boolean>(false);
  const [isEndDateChanged, setIsEndDateChanged] = useState<boolean>(false);

  // Fetch column grid data to find field names
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", SPENT_HOURS_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(SPENT_HOURS_SCHEMA_NAME),
  });

  const { data: spentHoursSchemaRecord } = useQuery({
    queryKey: ["schema", SPENT_HOURS_SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(SPENT_HOURS_SCHEMA_NAME),
    enabled: !!SPENT_HOURS_SCHEMA_NAME,
  });

  // Full open-project list for name resolution (no emp_id on this API; grid rows are already filtered by employee)
  const { data: projectOptions } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const { data: taskOptions } = useQuery({
    queryKey: ["task-dropdown", TASK_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(TASK_SCHEMA_NAME),
    enabled: !!TASK_SCHEMA_NAME,
  });

  // const { data: categoryOptions } = useQuery({
  //   queryKey: ["category-dropdown", TASK_CATEGORY_SCHEMA_NAME],
  //   queryFn: () => DropdownService.getDropdownData(TASK_CATEGORY_SCHEMA_NAME),
  //   enabled: !!TASK_CATEGORY_SCHEMA_NAME,
  // });

  // Format date for API (YYYY-MM-DDTHH:mm:ss)
  // Start date: 00:00:00, End date: 23:59:59
  const formatDateForAPI = (dateString: string, isEndDate: boolean = false) => {
    if (!dateString) {
      return null;
    }

    // Parse the date string (YYYY-MM-DD format)
    const [year, month, day] = dateString.split("-").map(Number);

    // Create date string with fixed time
    if (isEndDate) {
      // End date: 23:59:59
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T23:59:59`;
    } else {
      // Start date: 00:00:00
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`;
    }
  };

  // Fetch spent hours data with pagination
  const { data: spentHoursDataResponse, isLoading } = useQuery({
    queryKey: [
      "spent-hours-data",
      SPENT_HOURS_SCHEMA_NAME,
      startDate,
      endDate,
      employeeId,
      currentPage,
      pageSize,
    ],
    queryFn: () => {
      const paramsFilter: any = {};

      // Add date filters
      if (startDate) {
        const formattedStartDate = formatDateForAPI(startDate, false);
        if (formattedStartDate) {
          paramsFilter.date__gte = formattedStartDate;
        }
      }
      if (endDate) {
        const formattedEndDate = formatDateForAPI(endDate, true);
        if (formattedEndDate) {
          paramsFilter.date__lte = formattedEndDate;
        }
      }

      // Add employee ID filter
      if (employeeId) {
        paramsFilter.emp_id = employeeId;
      }

      paramsFilter.ordering = "-date";

      return DynamicModuleService.getItemsByPage(
        SPENT_HOURS_SCHEMA_NAME,
        currentPage,
        pageSize,
        paramsFilter
      );
    },
    enabled: !!SPENT_HOURS_SCHEMA_NAME,
  });

  // Extract data from response
  const timeEntries = useMemo(() => {
    if (!spentHoursDataResponse) {
      return [];
    }
    if (Array.isArray(spentHoursDataResponse)) {
      return spentHoursDataResponse;
    }
    if (
      spentHoursDataResponse.results &&
      Array.isArray(spentHoursDataResponse.results)
    ) {
      return spentHoursDataResponse.results;
    }
    return [];
  }, [spentHoursDataResponse]);

  // Get total count from response
  const totalCount = useMemo(() => {
    if (!spentHoursDataResponse) {
      return 0;
    }
    if (typeof spentHoursDataResponse.count === "number") {
      return spentHoursDataResponse.count;
    }
    // Fallback: if no count, use array length
    if (Array.isArray(spentHoursDataResponse)) {
      return spentHoursDataResponse.length;
    }
    if (
      spentHoursDataResponse.results &&
      Array.isArray(spentHoursDataResponse.results)
    ) {
      return spentHoursDataResponse.results.length;
    }
    return 0;
  }, [spentHoursDataResponse]);

  // Find field names from schema
  const findFieldName = useCallback(
    (searchTerms: string[], excludeNameSubstrings: string[] = []) => {
      if (!columnGridData?.grid) {
        return null;
      }
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          excludeNameSubstrings.some(
            ex => fieldName.includes(ex) ?? fieldTitle.includes(ex)
          )
        ) {
          continue;
        }
        if (
          searchTerms.some(
            term =>
              fieldName.includes(term.toLowerCase()) ??
              fieldTitle.includes(term.toLowerCase())
          )
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [columnGridData]
  );

  // Get field names
  const dateField = findFieldName(["date", "work_date", "entry_date"]);
  const projectField = findFieldName(["project", "project_id", "spentproject"]);
  const taskField = findFieldName(["task", "task_id", "spenttask"]);
  const descriptionField = findFieldName(["description", "desc"]);
  // Exclude "estimate*" so `estimatedhours` is not used as logged hours
  const hoursField = findFieldName(
    ["spent_hours", "total_hours", "hours", "hour"],
    ["estimate"]
  );
  const minutesField = findFieldName([
    "minutes",
    "minutetime",
    "total_minutes",
  ]);
  const categoryField = findFieldName(["category", "task_category"]);
  /** Display-only: API returns human-readable name on each row. */
  const subtaskNameField = findFieldName(["subtask_name"]);
  const statusField = findFieldName(["status", "approval_status"]);
  const estimatedHoursField = findFieldName([
    "estimatedhours",
    "estimated_hours",
  ]);
  const workedHoursField = findFieldName(
    ["work_hours", "worked_hours", "workedhours", "worked hours"],
    ["estimate"]
  );

  /** Prefer API `hour` / `minutetime` so estimatedhours never affects logged time. */
  const getLoggedHoursMinutes = useCallback(
    (entry: any) => {
      const hasDiscreteHour =
        entry.hour !== undefined &&
        entry.hour !== null &&
        String(entry.hour).trim() !== "";
      if (hasDiscreteHour) {
        return {
          hours: entry.hour,
          minutes:
            entry.minutetime ?? entry.minutes ?? entry.total_minutes ?? 0,
        };
      }
      return {
        hours:
          (hoursField && entry[hoursField]) ??
          entry.hours ??
          entry.total_hours ??
          0,
        minutes:
          (minutesField && entry[minutesField]) ??
          entry.minutes ??
          entry.minutetime ??
          entry.total_minutes ??
          0,
      };
    },
    [hoursField, minutesField]
  );

  const getEstimatedHoursDisplay = useCallback(
    (entry: any) => {
      const raw =
        (estimatedHoursField && entry[estimatedHoursField]) ??
        entry.estimatedhours ??
        entry.estimated_hours ??
        null;
      if (raw === null || raw === undefined || String(raw).trim() === "") {
        return "-";
      }
      return String(raw);
    },
    [estimatedHoursField]
  );

  const getWorkedHoursDisplay = useCallback(
    (entry: any) => {
      const raw =
        (workedHoursField && entry[workedHoursField]) ??
        entry.work_hours ??
        entry.worked_hours ??
        null;
      if (raw === null || raw === undefined || String(raw).trim() === "") {
        return "-";
      }
      return String(raw);
    },
    [workedHoursField]
  );

  const parseHoursToMinutes = useCallback((value: unknown): number | null => {
    if (value === null || value === undefined) {
      return null;
    }
    const text = String(value).trim();
    if (!text || text === "-") {
      return null;
    }

    // Handle H:MM format (e.g. "4:30")
    if (text.includes(":")) {
      const [hPart, mPart] = text.split(":");
      const h = Number(hPart);
      const m = Number(mPart);
      if (Number.isFinite(h) && Number.isFinite(m)) {
        return h * 60 + m;
      }
    }

    // Handle decimal/integer hour format (e.g. "4", "4.5", "4h")
    const numeric = Number(text.replace(/h/gi, "").trim());
    if (Number.isFinite(numeric)) {
      return Math.round(numeric * 60);
    }

    return null;
  }, []);

  const spentHoursStatusOptions = useMemo(
    () =>
      buildSpentHoursStatusOptions(
        spentHoursSchemaRecord,
        columnGridData?.grid,
        statusField ?? "status"
      ),
    [spentHoursSchemaRecord, columnGridData, statusField]
  );

  const getSubtaskNameDisplay = useCallback(
    (entry: any) => {
      const raw =
        (subtaskNameField && entry[subtaskNameField]) ??
        entry.subtask_name ??
        null;
      if (raw === null || raw === undefined || String(raw).trim() === "") {
        return "-";
      }
      return String(raw).trim();
    },
    [subtaskNameField]
  );

  // Helper functions to get names by ID
  const getProjectNameById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return "-";
      }
      const project = projectOptions?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
      return project?.label ?? id;
    },
    [projectOptions]
  );

  const getTaskNameById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return "-";
      }
      const task = taskOptions?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
      return task?.label ?? id;
    },
    [taskOptions]
  );

  // const getCategoryNameById = useCallback(
  //   (id: string | number | null | undefined) => {
  //     if (!id || id === "-") {
  //       return "-";
  //     }
  //     const category = categoryOptions?.find(
  //       (item: { value: string; label: string }) =>
  //         String(item.value) === String(id)
  //     );
  //     return category?.label ?? id;
  //   },
  //   [categoryOptions]
  // );

  // Format hours and minutes
  const formatHours = (
    hours: number | string | null | undefined,
    minutes: number | string | null | undefined
  ) => {
    const h = hours ? Number(hours) : 0;
    const m = minutes ? Number(minutes) : 0;
    if (h === 0 && m === 0) {
      return "-";
    }
    if (m === 0) {
      return `${h}h`;
    }
    if (h === 0) {
      return `${m}m`;
    }
    return `${h}h ${m}m`;
  };

  // Format total hours for header (e.g., "09 Hrs. 30 Min")
  const formatTotalHours = (totalHours: number, totalMinutes: number) => {
    const hoursStr = String(Math.floor(totalHours)).padStart(2, "0");
    const minutesStr = String(totalMinutes).padStart(2, "0");
    return `${hoursStr} Hrs. ${minutesStr} Min`;
  };

  // Group entries by date
  const groupedByDate = useMemo(() => {
    const groups: Record<
      string,
      {
        date: string;
        dateKey: string;
        entries: any[];
        totalHours: number;
        totalMinutes: number;
      }
    > = {};

    timeEntries.forEach((entry: any) => {
      const date =
        (dateField && entry[dateField]) ?? entry.date ?? entry.work_date;

      if (!date) {
        return;
      }

      // Format date inline
      let dateStr = "-";
      try {
        const d = new Date(date);
        if (!isNaN(d.getTime())) {
          dateStr = d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        } else {
          dateStr = String(date);
        }
      } catch {
        dateStr = String(date);
      }

      const dateKey = dateStr; // Use formatted date as key

      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: dateStr,
          dateKey,
          entries: [],
          totalHours: 0,
          totalMinutes: 0,
        };
      }

      const { hours, minutes } = getLoggedHoursMinutes(entry);

      const h =
        hours === "" || hours === null || hours === undefined
          ? 0
          : Number(hours);
      const m =
        minutes === "" || minutes === null || minutes === undefined
          ? 0
          : Number(minutes);

      groups[dateKey].entries.push(entry);
      groups[dateKey].totalHours += h;
      groups[dateKey].totalMinutes += m;
    });

    // Convert minutes overflow to hours
    Object.values(groups).forEach(group => {
      const additionalHours = Math.floor(group.totalMinutes / 60);
      group.totalHours += additionalHours;
      group.totalMinutes = group.totalMinutes % 60;
    });

    // Sort by date (newest first)
    return Object.values(groups).sort((a, b) => {
      // Parse dates for comparison
      const dateA = a.entries[0]?.[dateField ?? "date"] ?? a.date;
      const dateB = b.entries[0]?.[dateField ?? "date"] ?? b.date;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }, [timeEntries, dateField, getLoggedHoursMinutes]);

  // Handle delete
  const handleDelete = async (id: string | undefined) => {
    if (!id) {
      notify.error("Record ID is missing");
      return;
    }
    try {
      const response = await DynamicModuleService.deleteItem(
        SPENT_HOURS_SCHEMA_NAME,
        String(id)
      );
      if (response) {
        // notify.success("Spent hours record deleted successfully");
        queryClient.invalidateQueries({
          queryKey: ["spent-hours-data", SPENT_HOURS_SCHEMA_NAME],
        });
      }
    } catch (error: any) {
      notify.error(error?.message ?? "Failed to delete record");
    }
  };

  // Handle edit - scroll to form and set edit mode
  const handleEdit = (id: string | undefined) => {
    if (!id) {
      notify.error("Record ID is missing");
      return;
    }

    // Dispatch custom event first to trigger form loading
    window.dispatchEvent(
      new CustomEvent("editSpentHours", { detail: { id: String(id) } })
    );

    // Scroll to the Add Spent Hours form with a slight delay to ensure form is ready
    setTimeout(() => {
      const formElement = document.querySelector(
        "[data-spent-hours-form]"
      ) as HTMLElement;
      if (formElement) {
        // Calculate scroll position with offset to ensure full form is visible
        const rect = formElement.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const offset = 80; // Offset from top to ensure header/nav doesn't cover form
        const targetPosition = rect.top + scrollTop - offset;

        window.scrollTo({
          top: Math.max(0, targetPosition),
          behavior: "smooth",
        });

        // Add a subtle highlight effect
        formElement.classList.add("ring-2", "ring-primary", "ring-offset-2");
        setTimeout(() => {
          formElement.classList.remove(
            "ring-2",
            "ring-primary",
            "ring-offset-2"
          );
        }, 2000);
      }
    }, 100);
  };

  // Handle add - scroll to form and pre-fill with project, task, and category
  const handleAdd = (entry: any) => {
    // Extract field values using the same logic as display
    const projectId =
      (projectField && entry[projectField]) ??
      entry.project_id ??
      entry.project ??
      entry.spentproject;
    const taskId =
      (taskField && entry[taskField]) ??
      entry.task_id ??
      entry.task ??
      entry.spenttask;
    const category =
      (categoryField && entry[categoryField]) ?? entry.category ?? "-";

    // Scroll to the Add Spent Hours form
    const formElement = document.querySelector("[data-spent-hours-form]");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Dispatch custom event to notify parent component with pre-filled fields
    window.dispatchEvent(
      new CustomEvent("addSpentHoursWithFields", {
        detail: {
          project: projectId ? String(projectId) : undefined,
          task: taskId ? String(taskId) : undefined,
          category: category && category !== "-" ? String(category) : undefined,
        },
      })
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (timeEntries.length === 0) {
    return (
      <div className="rounded-xl border bg-white">
        <div className="flex items-center justify-between border-b p-4">
          <div className="flex items-center gap-3">
            <h3 className="text-base font-semibold text-gray-900">
              Time Entry History
            </h3>
            <div className="flex items-center gap-2">
              <div className="relative inline-flex items-center">
                <label
                  htmlFor="start-date"
                  className="mr-1 text-xs text-gray-600"
                >
                  Start:
                </label>
                <input
                  type="date"
                  id="start-date"
                  value={startDate}
                  onChange={e => {
                    setStartDate(e.target.value);
                    setIsStartDateChanged(true);
                    setCurrentPage(1); // Reset to first page when date changes
                  }}
                  className="border-border-input bg-background-input focus:border-primary focus:ring-primary rounded-lg border px-3 py-1 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-1"
                />
                {isStartDateChanged && startDate && (
                  <button
                    onClick={() => {
                      const defaults = getDefaultDates();
                      setStartDate(defaults.startDate);
                      setIsStartDateChanged(false);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Reset to default"
                    type="button"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <div className="relative inline-flex items-center">
                <label
                  htmlFor="end-date"
                  className="mr-1 text-xs text-gray-600"
                >
                  End:
                </label>
                <input
                  type="date"
                  id="end-date"
                  value={endDate}
                  onChange={e => {
                    setEndDate(e.target.value);
                    setIsEndDateChanged(true);
                    setCurrentPage(1); // Reset to first page when date changes
                  }}
                  className="border-border-input bg-background-input focus:border-primary focus:ring-primary rounded-lg border px-3 py-1 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-1"
                />
                {isEndDateChanged && endDate && (
                  <button
                    onClick={() => {
                      const defaults = getDefaultDates();
                      setEndDate(defaults.endDate);
                      setIsEndDateChanged(false);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Reset to default"
                    type="button"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
          <span className="text-sm text-gray-500">
            {timeEntries.length} Records
          </span>
        </div>
        <div className="py-8 text-center text-gray-500">No records found</div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Style for rendered ReactQuill HTML content in activity log */
        .ql-editor-content {
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        .ql-editor-content p {
          margin: 0 0 8px 0;
          line-height: 1.6;
        }
        
        .ql-editor-content p:last-child {
          margin-bottom: 0;
        }
        
        .ql-editor-content ul,
        .ql-editor-content ol {
          margin: 8px 0;
          padding-left: 24px;
        }
        
        .ql-editor-content li {
          margin: 4px 0;
          line-height: 1.6;
        }
        
        .ql-editor-content strong {
          font-weight: 600;
        }
        
        .ql-editor-content em {
          font-style: italic;
        }
        
        .ql-editor-content u {
          text-decoration: underline;
        }
        
        .ql-editor-content s {
          text-decoration: line-through;
        }
        
        .ql-editor-content a {
          color: #3b82f6;
          text-decoration: underline;
        }
        
        .ql-editor-content a:hover {
          color: #2563eb;
        }
        
        .ql-editor-content h1,
        .ql-editor-content h2,
        .ql-editor-content h3 {
          margin: 12px 0 8px 0;
          font-weight: 600;
        }
        
        .ql-editor-content h1 {
          font-size: 1.5em;
        }
        
        .ql-editor-content h2 {
          font-size: 1.3em;
        }
        
        .ql-editor-content h3 {
          font-size: 1.1em;
        }
      `}</style>
      <div className="rounded-xl border bg-white">
        <div className="flex items-center justify-between border-b p-3 md:p-4">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-base font-semibold text-gray-900">
              Time Entry History
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative inline-flex items-center">
                <label
                  htmlFor="start-date"
                  className="mr-1 text-xs text-gray-600"
                >
                  Start:
                </label>
                <input
                  type="date"
                  id="start-date"
                  value={startDate}
                  onChange={e => {
                    setStartDate(e.target.value);
                    setIsStartDateChanged(true);
                    setCurrentPage(1); // Reset to first page when date changes
                  }}
                  className="border-border-input bg-background-input focus:border-primary focus:ring-primary rounded-lg border px-3 py-1 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-1"
                />
                {isStartDateChanged && startDate && (
                  <button
                    onClick={() => {
                      const defaults = getDefaultDates();
                      setStartDate(defaults.startDate);
                      setIsStartDateChanged(false);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Reset to default"
                    type="button"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <div className="relative inline-flex items-center">
                <label
                  htmlFor="end-date"
                  className="mr-1 text-xs text-gray-600"
                >
                  End:
                </label>
                <input
                  type="date"
                  id="end-date"
                  value={endDate}
                  onChange={e => {
                    setEndDate(e.target.value);
                    setIsEndDateChanged(true);
                    setCurrentPage(1); // Reset to first page when date changes
                  }}
                  className="border-border-input bg-background-input focus:border-primary focus:ring-primary rounded-lg border px-3 py-1 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-1"
                />
                {isEndDateChanged && endDate && (
                  <button
                    onClick={() => {
                      const defaults = getDefaultDates();
                      setEndDate(defaults.endDate);
                      setIsEndDateChanged(false);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Reset to default"
                    type="button"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
          <span className="text-sm text-gray-500">
            {timeEntries.length} Records
          </span>
        </div>

        <Collapse
          defaultActiveKey={
            groupedByDate.length > 0 ? [groupedByDate[0].dateKey] : []
          }
          items={groupedByDate.map(group => ({
            key: group.dateKey,
            label: (
              <div className="flex w-full items-center justify-between pr-4">
                <span className="text-primary font-medium">{group.date}</span>
                <span className="text-primary text-sm font-semibold">
                  Total:{" "}
                  <span className="rounded-md border-2 border-gray-300 px-2 py-0">
                    {formatTotalHours(group.totalHours, group.totalMinutes)}
                  </span>
                </span>
              </div>
            ),
            children: (
              <div className="overflow-x-auto py-2">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      {/* <TableHead className="w-12 ps-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </TableHead> */}
                      <TableHead className="font-semibold text-gray-700">
                        Project
                      </TableHead>
                      <TableHead className="font-semibold text-gray-700">
                        Task
                      </TableHead>
                      <TableHead className="font-semibold text-gray-700">
                        Sub task
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Estimated hours
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Worked hours
                      </TableHead>
                      <TableHead className="font-semibold text-gray-700">
                        Status
                      </TableHead>
                      {/* <TableHead className="font-semibold text-gray-700">
                        Activity
                      </TableHead> */}
                      <TableHead className="font-semibold text-gray-700">
                        Description
                      </TableHead>
                      <TableHead className="text-right font-semibold text-gray-700">
                        Hours
                      </TableHead>
                      <TableHead className="pr-8 text-right font-semibold text-gray-700">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.entries.map((entry: any) => {
                      const recordId =
                        entry?.id ?? entry?.ID ?? entry?.Id ?? entry?._id;
                      const projectId =
                        (projectField && entry[projectField]) ??
                        entry.project_id ??
                        entry.project;
                      const taskId =
                        (taskField && entry[taskField]) ??
                        entry.task_id ??
                        entry.task;
                      const description =
                        (descriptionField && entry[descriptionField]) ??
                        entry.description ??
                        entry.desc ??
                        "-";
                      const { hours, minutes } = getLoggedHoursMinutes(entry);
                      // const category =
                      //   (categoryField && entry[categoryField]) ??
                      //   entry.category ??
                      //   "-";
                      const statusRaw =
                        (statusField && entry[statusField]) ??
                        entry.status ??
                        null;
                      const statusLabelResolved =
                        statusRaw !== null && String(statusRaw).trim() !== ""
                          ? (spentHoursStatusOptions.find(
                              o => String(o.value) === String(statusRaw)
                            )?.label ?? String(statusRaw))
                          : null;
                      const estimatedDisplay = getEstimatedHoursDisplay(entry);
                      const workedDisplay = getWorkedHoursDisplay(entry);
                      const estimatedMinutes =
                        parseHoursToMinutes(estimatedDisplay);
                      const workedMinutes = parseHoursToMinutes(workedDisplay);
                      const workedHoursColorClass =
                        estimatedMinutes !== null &&
                        workedMinutes !== null &&
                        workedMinutes > estimatedMinutes
                          ? "text-red-600"
                          : "text-gray-700";

                      return (
                        <TableRow key={recordId} className="hover:bg-gray-50">
                          {/* <TableCell>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300"
                          />
                        </TableCell> */}
                          <TableCell className="ps-4 text-gray-700">
                            {getProjectNameById(projectId)}
                          </TableCell>
                          <TableCell className="text-gray-700">
                            {getTaskNameById(taskId)}
                          </TableCell>
                          <TableCell className="max-w-[12rem] text-gray-700">
                            {getSubtaskNameDisplay(entry)}
                          </TableCell>
                          <TableCell className="text-center text-gray-700">
                            {estimatedDisplay}
                          </TableCell>
                          <TableCell
                            className={`text-center ${workedHoursColorClass}`}
                          >
                            {workedDisplay}
                          </TableCell>
                          <TableCell>
                            {statusLabelResolved ? (
                              spentHoursStatusPill(
                                statusLabelResolved,
                                String(statusRaw)
                              )
                            ) : (
                              <span className="pl-6">--</span>
                            )}
                          </TableCell>
                          {/* <TableCell>
                            {category && category !== "-" ? (
                              <Badge className="bg-primary border-0 text-xs text-white">
                                {getCategoryNameById(category)}
                              </Badge>
                            ) : (
                              "-"
                            )}
                          </TableCell> */}
                          <TableCell className="max-w-xs text-gray-600">
                            {description && description !== "-" ? (
                              <div
                                className="ql-editor-content prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    typeof description === "string"
                                      ? description
                                      : String(description),
                                }}
                                style={{
                                  fontSize: "14px",
                                  lineHeight: "1.6",
                                  color: "#374151",
                                }}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium text-gray-900">
                            {formatHours(hours, minutes)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-0">
                              <CustomButton
                                icon={<Plus size={14} />}
                                type="text"
                                onClick={() => handleAdd(entry)}
                                title="Add"
                              />
                              <CustomButton
                                icon={<Edit2 size={14} />}
                                type="text"
                                onClick={() => handleEdit(recordId)}
                                title="Edit"
                              />
                              <Popconfirm
                                title="Are you sure you want to delete this record?"
                                onConfirm={() => handleDelete(recordId)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <CustomButton
                                  danger
                                  icon={<Trash size={14} />}
                                  type="text"
                                  title="Delete"
                                />
                              </Popconfirm>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ),
          }))}
          className="border-0 bg-transparent"
        />
        {/* Pagination */}
        <div className="flex items-center justify-end border-t p-4">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalCount}
            onChange={(page, size) => {
              if (size && size !== pageSize) {
                setCurrentPage(1);
                setPageSize(size);
              } else {
                setCurrentPage(page);
              }
            }}
            onShowSizeChange={(_current, size) => {
              setCurrentPage(1);
              setPageSize(size);
            }}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} items`
            }
            pageSizeOptions={["300", "500"]}
            disabled={isLoading}
          />
        </div>
      </div>
    </>
  );
};

export default ActivityLogTab;
